import { SearchOutlined } from '@ant-design/icons';
import { DatePicker, Form, Input, Row, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import { getAllSubcriptionOrders } from 'src/api/register_and_cancel_and_load_package';
import BoxWithLabel from 'src/components/BoxWithLabel/BoxWithLabel';
import TableContent from 'src/components/CTable';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { setModalState } from 'src/redux/slice/modal.store';
import { orderStatus } from 'src/shared/common/package_assigned';
import { UnitDatacodeEnum } from 'src/shared/enum/load_datacode';
import { TransactionHistoryType } from 'src/shared/enum/localpay';
import { ISubcriptionDataOrderList } from 'src/shared/interface/register_and_cancel_and_load';
import { convertNumberByUnit, parseQueryString } from 'src/utils';

function DataAccountListPage() {
  const size = useWindowSize();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { openTabs } = useAppSelector((state) => state.global);
  const [data, setData] = useState<PaginationResponse<ISubcriptionDataOrderList[]>>();
  const [sortColumn, setSortColumn] = useState<ISortColumn>();
  const [unit, setUnit] = useState<string>('GB');
  const [_, setSearchParams] = useSearchParams();
  const [form] = useForm();

  const fetchListSubcriptionOrders = async () => {
    const params = parseQueryString(location.search);
    const res = await getAllSubcriptionOrders(params);
    if (res) {
      setData(res);
    }
  };
  useEffect(() => {
    fetchListSubcriptionOrders();
    setSortColumn(undefined);
    form.setFieldValue('currentBalance', 102443243);
  }, [location.search]);

  useEffect(() => {
    const rs = convertNumberByUnit(24, 'GB', unit);
    form.setFieldValue('currentBalance', rs);
  }, [unit]);

  function handleFormSearch(value: any) {
    let values = { ...value };
    values = {
      ...values,
      dateFrom:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : value.dateFrom ?? undefined,
      dateTo:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : value.dateTo ?? undefined,
      dateRange: undefined,
    };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });
    setSearchParams({ pageIndex: 1, pageSize: 10, ...values });
  }

  const handleDetailCancel = (order: any) => {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'data-account/detail' + '?id=' + order.id,
      }),
    );
    if (!openTabs.find((tab) => tab.key === 'data-account/detail' + '?id=' + order.id)) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Xem chi tiết Tài khoản data ' + order.id,
              children: '',
              key: 'data-account/detail' + '?id=' + order.id,
              closable: true,
            },
          ],
        }),
      );
    }
    navigate(`/management/data-account/detail?id=${order.id}`);
  };

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
    {
      name: 'Mã đơn hàng',
      fieldName: 'id',
      renderSpecialCard: (order: ISubcriptionDataOrderList) => (
        <button
          className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent text-blue-400 font-semibold`}
          onClick={() => {
            if (order.extraDataObject.totalData === 1048576) {
              dispatch(
                setModalState({
                  open: true,
                  content: 'export-data-info',
                  selectedHistory: order.id,
                  width: size.width <= 768 ? '95%' : '50%',
                }),
              );
            } else {
              handleDetailCancel(order);
            }
          }}
        >
          {order.id ?? ''}
        </button>
      ),
    },
    {
      name: 'Loại đơn hàng',
      fieldName: 'orderType',
      isHideSort: true,
    },
    {
      name: 'Tổng dung lượng',
      fieldName: '',
      isHideSort: true,
      renderSpecialCard: (record: ISubcriptionDataOrderList) => {
        return (
          <span className='whitespace-nowrap'>
            {convertNumberByUnit(record.extraDataObject.totalData, 'KB', 'GB')}
          </span>
        );
      },
    },
    {
      name: 'Đơn vị tính',
      fieldName: '',
      isHideSort: true,
      renderSpecialCard: () => {
        return <span className='whitespace-nowrap'>{'GB'}</span>;
      },
    },
    {
      name: 'Số dư sau giao dịch (KB)',
      fieldName: 'balanceAfter',
      isHideSort: true,
      renderSpecialCard: (record: ISubcriptionDataOrderList) => {
        return (
          <span className='whitespace-nowrap'>
            {convertNumberByUnit(record.extraDataObject.totalData, 'KB', unit)}
          </span>
        );
      },
    },
    {
      name: 'Trạng thái',
      fieldName: 'statusString',
      renderSpecialCard: (record: ISubcriptionDataOrderList) => {
        const status = orderStatus.find((item) => item.value === record.statusString);
        return <span className='whitespace-nowrap'>{status?.label ?? ''}</span>;
      },
      isHideSort: true,
    },
    {
      name: 'Thời gian tạo',
      fieldName: 'createdDt',
      isHideSort: true,
    },
    {
      name: 'Người tạo',
      fieldName: 'createdBy',
      isHideSort: true,
    },
  ];

  return (
    <Fragment>
      <Form onValuesChange={handleFormSearch} form={form}>
        <BoxWithLabel title='Thông tin tài khoản data' classNames='mt-4'>
          <div className='md:flex md:gap-4 md:flex-wrap'>
            <Form.Item name='accountId' label='Mã tài khoản'>
              <Input allowClear size='middle' readOnly />
            </Form.Item>
            <Form.Item name='currentBalance' label='Số dư hiện tại'>
              <Input allowClear size='middle' readOnly />
            </Form.Item>
            <Form.Item shouldUpdate>
              <Select
                onSelect={(e) => setUnit(e)}
                value={unit}
                defaultValue={'GB'}
                options={[
                  {
                    value: UnitDatacodeEnum.KiloByte,
                  },
                  {
                    value: UnitDatacodeEnum.MegaByte,
                  },
                  {
                    value: UnitDatacodeEnum.GigaByte,
                  },
                  {
                    value: UnitDatacodeEnum.TeraByte,
                  },
                ]}
              />
            </Form.Item>

            <Form.Item name='createAt' label='Ngày tạo tài khoản'>
              <DatePicker
                format={'DD/MM/YYYY HH:mm:ss'}
                size='middle'
                inputReadOnly={true}
                open={false}
                allowClear={false}
              />
            </Form.Item>
          </div>
        </BoxWithLabel>

        <Row className='mt-5 flex items-center gap-3'>
          <Form.Item name='search'>
            <Input
              allowClear
              className='min-w-[300px]'
              prefix={<SearchOutlined />}
              placeholder='Mã đơn hàng'
            />
          </Form.Item>
          <Form.Item name='type' label='Loại đơn hàng'>
            <Select
              showSearch
              className='min-w-[150px]'
              placeholder='Vui lòng chọn'
              defaultValue={''}
            >
              <Select.Option value=''>Tất cả</Select.Option>
              <Select.Option value={TransactionHistoryType.Datacode}>Nạp data</Select.Option>
              <Select.Option value={'1'}>Nhập data</Select.Option>
              <Select.Option value={'2'}>Điều chỉnh tăng</Select.Option>
              <Select.Option value={'3'}>Điều chỉnh giảm</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name='dateRange' label='Ngày tạo'>
            <DatePicker.RangePicker
              disabledDate={(current) => current && current >= moment().endOf('day')}
              placeholder={['từ', 'đến']}
              format={['DD/MM/YYYY', 'DD/MM/YYYY']}
            />
          </Form.Item>
        </Row>
      </Form>

      <div className='mt-4 overflow-hidden'>
        <TableContent
          bordered
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Không tìm thấy kết quả' }}
          dataSource={data?.results ?? []}
          columns={columns}
          totalRecord={data?.rowCount ?? 0}
          sortColumn={sortColumn}
          size='middle'
          onSort={(sort: ISortColumn) => {
            const pagination = parseQueryString(location.search);
            handleFormSearch({
              ...pagination,
              pageIndex: pagination.pageIndex ?? 1,
              pageSize: pagination.pageSize ?? 10,
              sortField: sort.fieldName,
              asc:
                sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
            });
          }}
        />
      </div>
    </Fragment>
  );
}

export default DataAccountListPage;
